import React from "react";

interface AIContentMarkupProps {
  children: React.ReactNode;
  contentType?: 'article' | 'service' | 'location' | 'product' | 'homepage';
  title?: string;
  description?: string;
  category?: string;
  benefits?: string[];
  features?: string[];
  location?: string;
}

export const AIContentMarkup = ({ 
  children, 
  contentType = 'article',
  title,
  description,
  category,
  benefits,
  features,
  location
}: AIContentMarkupProps) => {
  const getSchemaType = () => {
    switch (contentType) {
      case 'service':
        return 'https://schema.org/Service';
      case 'location':
        return 'https://schema.org/Place';
      case 'product':
        return 'https://schema.org/Product';
      case 'homepage':
        return 'https://schema.org/WebPage';
      default:
        return 'https://schema.org/Article';
    }
  };

  const getItemProps = () => {
    const baseProps = {
      itemScope: true,
      itemType: getSchemaType()
    };

    return baseProps;
  };

  return (
    <div {...getItemProps()}>
      {title && (
        <meta itemProp="name" content={title} />
      )}
      {description && (
        <meta itemProp="description" content={description} />
      )}
      {category && (
        <meta itemProp="category" content={category} />
      )}
      {location && (
        <meta itemProp="address" content={location} />
      )}
      {benefits && benefits.map((benefit, index) => (
        <meta key={`benefit-${index}`} itemProp="benefit" content={benefit} />
      ))}
      {features && features.map((feature, index) => (
        <meta key={`feature-${index}`} itemProp="feature" content={feature} />
      ))}
      
      <div itemProp="mainContent">
        {children}
      </div>
    </div>
  );
};

// Semantic HTML components for better AI understanding
export const AIHeading = ({ 
  level = 1, 
  children, 
  semantic = 'headline' 
}: { 
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  children: React.ReactNode;
  semantic?: 'headline' | 'subheadline' | 'section' | 'subsection';
}) => {
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;
  
  return (
    <Tag itemProp={semantic} className={`heading-${level}`}>
      {children}
    </Tag>
  );
};

export const AIText = ({ 
  children, 
  semantic = 'text',
  highlight = false 
}: { 
  children: React.ReactNode;
  semantic?: 'text' | 'description' | 'summary' | 'benefit' | 'feature';
  highlight?: boolean;
}) => {
  return (
    <p 
      itemProp={semantic} 
      className={highlight ? 'highlighted-text' : ''}
      data-ai-content={semantic}
    >
      {children}
    </p>
  );
};

export const AIList = ({ 
  items, 
  type = 'benefits',
  ordered = false 
}: { 
  items: string[];
  type?: 'benefits' | 'features' | 'steps' | 'services';
  ordered?: boolean;
}) => {
  const ListTag = ordered ? 'ol' : 'ul';
  
  return (
    <ListTag itemScope itemType="https://schema.org/ItemList" data-list-type={type}>
      <meta itemProp="numberOfItems" content={String(items.length)} />
      {items.map((item, index) => (
        <li 
          key={index} 
          itemProp="itemListElement" 
          itemScope 
          itemType="https://schema.org/ListItem"
        >
          <meta itemProp="position" content={String(index + 1)} />
          <span itemProp="name">{item}</span>
        </li>
      ))}
    </ListTag>
  );
};

export const AICallToAction = ({ 
  children, 
  actionType = 'contact',
  urgency = 'normal' 
}: { 
  children: React.ReactNode;
  actionType?: 'contact' | 'quote' | 'learn' | 'schedule';
  urgency?: 'low' | 'normal' | 'high';
}) => {
  return (
    <div 
      itemScope 
      itemType="https://schema.org/Action"
      data-action-type={actionType}
      data-urgency={urgency}
      className="ai-cta"
    >
      <meta itemProp="actionStatus" content="PotentialActionStatus" />
      <div itemProp="object">
        {children}
      </div>
    </div>
  );
};